import {
  ActivityStatus,
  GradeActivityDto,
  GradeAssetDto,
  GraderFeedbackDto,
} from "@cca/components";
import appConfig from "@core/config";
import { SortOrderV2 } from "@core/enums";
import type {
  ActivityContentRevisionDto,
  ActivityDto,
  AssetProgressDto,
  CourseGradeResult,
  GradebookDto,
  PaginationParams,
  SortingParams,
} from "@core/typesV2";
import { UngradedWorkDto } from "@core/typesV2";
import { NavigateFunction } from "react-router-dom";
import { z } from "zod";

import { type TokenHandlers } from "../services/axiosService";
import AxiosService from "./axiosServiceV2";

export default class ActivityProgressService extends AxiosService {
  constructor(tokenHandlers: TokenHandlers, navigateTo: NavigateFunction) {
    super(
      `${appConfig.LEARNING_BASE_URL}/api/v1/courses`,
      tokenHandlers,
      navigateTo,
    );
  }

  getActivities(courseId: string, dayId: string, learnerId?: string) {
    return this.get<ActivityDto[]>(
      `${courseId}/outline/${dayId}/activities${learnerId ? `?requestedUserId=${learnerId}` : ""}`,
    );
  }

  getAssetProgress(courseId: string, activityId: string, learnerId?: string) {
    return this.get<AssetProgressDto[]>(
      `${courseId}/activities/${activityId}/assets${learnerId ? `?requestedUserId=${learnerId}` : ""}`,
      undefined,
      { skipNavigateIfError: true },
    );
  }

  getActivityContent(courseId: string, activityId: string, learnerId?: string) {
    return this.get<ActivityContentRevisionDto>(
      `${courseId}/activities/${activityId}/content${learnerId ? `?requestedUserId=${learnerId}` : ""}`,
      undefined,
      { skipNavigateIfError: true },
    );
  }

  updateAssetProgress(
    courseId: string,
    activityId: string,
    assetProgress: AssetProgressDto[],
  ) {
    return this.post<AssetProgressDto[], AssetProgressDto[]>(
      `${courseId}/activities/${activityId}/assets`,
      assetProgress,
    );
  }

  saveGradeAssets(
    courseId: string,
    activityId: string,
    learnerId: string,
    gradeAssets: GradeAssetDto[],
  ) {
    return this.put(
      `${courseId}/activities/${activityId}/learners/${learnerId}/grade-assets`,
      gradeAssets,
    );
  }

  saveGradeActivity(
    courseId: string,
    activityId: string,
    learnerId: string,
    gradeActivity: GradeActivityDto,
  ) {
    return this.put(
      `${courseId}/activities/${activityId}/learners/${learnerId}/grade-activity`,
      gradeActivity,
    );
  }

  public submitActivityGrade(
    courseId: string,
    activityId: string,
    learnerId: string,
  ): Promise<CourseGradeResult> {
    return this.patch(
      `${courseId}/activities/${activityId}/learners/${learnerId}/grade`,
      null,
    );
  }

  public editActivityGrade(
    courseId: string,
    activityId: string,
    learnerId: string,
  ): Promise<void | null> {
    return this.patch(
      `${courseId}/activities/${activityId}/learners/${learnerId}/edit`,
      null,
    );
  }

  public resetActivity(
    courseId: string,
    activityId: string,
    learnerId: string,
  ): Promise<void | null> {
    return this.patch(
      `${courseId}/activities/${activityId}/learners/${learnerId}/reset`,
      null,
    );
  }

  getGradebooks(
    courseId?: string,
    learnerId?: string,
    sortBy?: string,
    sortOrder?: SortOrderV2,
  ) {
    return this.get<GradebookDto>(`${courseId}/gradebooks/${learnerId}`, {
      params: { sortBy, sortOrder },
    });
  }

  public startActivity(
    courseId: string,
    activityId: string,
  ): Promise<void | null> {
    return this.patch(`${courseId}/activity/${activityId}/start`, null);
  }

  public submitActivity(
    courseId: string,
    activityId: string,
  ): Promise<void | null> {
    return this.patch(`${courseId}/activity/${activityId}/submit`, null);
  }

  public submitAsset(
    courseId: string,
    activityId: string,
    assetId: string,
  ): Promise<GraderFeedbackDto | null> {
    return this.post(
      `${courseId}/activities/${activityId}/assets/${assetId}/grade`,
      null,
    );
  }

  getCourseUngradedWorkCount(courseId: string) {
    return this.head(`${courseId}/ungraded-works`).then((response) =>
      z.coerce.number().parse(response.headers["x-total-count"]),
    );
  }

  public getCourseUngradedWorks(
    courseId: string,
    activityStatus: ActivityStatus,
    paginationParams: PaginationParams,
    params: SortingParams,
  ): Promise<UngradedWorkDto[] | null> {
    return this.get<UngradedWorkDto[]>(`${courseId}/ungraded-works`, {
      params: { activityStatus, ...paginationParams, ...params },
    });
  }
}
